import * as React from 'react'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import { useAuth } from 'react-oidc-context'
import { connect, useSelector, type ConnectedProps } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import LanguageStrings from '../../../../i18n/locales'
import { type RootState } from '../../../../store'
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { fetchGatewayList, fetchAssetInfo } from '../../redux/actionCreators'
import { type GatewayListState } from '../../redux/gatewayListSlice'
import { type AssetInfoState } from '../../redux/assetInfoSlice'
import EnhancedTable, { type Sort } from './GatewayTable'
import AssetInformation from '../AssetInformation'
import { Button } from '../../../../components/Button'
import { downloadImage } from '../../../device-management/redux/actionCreators'
import type { DownloadImageState } from '../../../device-management/redux/downloadImageSlice'
import AddAssetDummy from '../../../../assets/AddDevice/image.png'

export interface State extends SnackbarOrigin {
  open: boolean
}

const GatewayListStrings = LanguageStrings().GatewayManagementCollectionStrings.GatewayListComponent
const DashboardStrings = LanguageStrings().Dashboard

// these values are hardcoded because the hierarchical link is not implemented based on priority. And, that they will be fetched from backend once the links are established
const assetTypeId = 'fdd42da4-4a13-4f57-9e1a-208d95c7f213'

const AssetInfoStyle: React.CSSProperties = {
  width: '99%',
  color: '#F0F0F0',
  padding: '1em',
  backgroundColor: '#272727',
  borderRadius: '5px',
  fontWeight: '500',
  marginBottom: '25px'
}
const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}

function GatewayList (props: PropsFromRedux): JSX.Element {
  const auth = useAuth()
  const navigate = useNavigate()
  const stateLocation = useLocation()
  const { parentAssetId } = useParams() as { parentAssetId: string }
  const [imageBinaryData, setImageBinaryData] = React.useState<any>(AddAssetDummy)
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({
    order: 'asc',
    orderBy: ''
  })
  const operationPermissions: OperationPermissionState = useSelector(
    (state: RootState) => state.operationPermissions
  )

  const rowsPerPage = 10
  interface StateLocation {
    parentSiteId: string
  }
  const navigateState = stateLocation.state as StateLocation | null
  /* istanbul ignore next */
  const parentSiteId = (navigateState != null) ? navigateState.parentSiteId : ''

  /* istanbul ignore next */
  React.useEffect(() => {
    (parentSiteId === '' || parentSiteId === undefined || parentSiteId === null) && navigate('/site-list')
  }, [parentSiteId])

  function handleBreadCrumbsClick (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  React.useEffect(() => {
    props.fetchAssetInfo(parentAssetId)
    props.downloadImage(parentAssetId, 'fdd42da4-4a13-4f57-9e1a-208d95c7f215', '')
  }, [])

  React.useEffect(() => {
    // istanbul ignore else
    if (props?.downloadImageState?.downloadImage?.binaryData !== '') {
      setImageBinaryData(props?.downloadImageState?.downloadImage)
    }
  }, [props?.downloadImageState?.downloadImage?.binaryData])

  return (
    <div>
      <div role="presentation" data-testid="breadcrumbs" onClick={handleBreadCrumbsClick} className="AmiHeading">
        <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
          onClick={() => { navigate(-1) }}
        />
        <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
          <Link aria-label='navBreadcrumb1' className='assetmgmtli' data-testid="bc-link-1" underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
            {DashboardStrings.HOME}
          </Link>
          <Link aria-label='navBreadcrumb2' className='assetmgmtli' data-testid="bc-link-2" underline='none' color='inherit' onClick={() => { navigate('/site-list') }} >
            {DashboardStrings.SiteManagement}
          </Link>
          <Link aria-label='navBreadcrumb3' className='assetmgmtli' data-testid="bc-link-3" underline='none' color='inherit'
            onClick={() => { navigate(`/thermal-asset-list/${parentSiteId}`) }} >
            {DashboardStrings.AssetManagement}
          </Link>
          <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{DashboardStrings.GatewayManagement}</Typography>
        </Breadcrumbs>
      </div>
      <div className="ArrowGatewayList">
        <div>
          <span
            data-testid="gatewayHistoryDetails"
            className="gatewayMgtGatewaylist"
            style={{ fontWeight: '700' }}
          >
            {GatewayListStrings.Gateway}
          </span>
        </div>
      </div>
      <br />
      <div style={AssetInfoStyle}>
        <AssetInformation parentSiteId={parentSiteId} assetInfo={props.assetInfo} imageBinaryData={imageBinaryData} />
      </div>
      <div className="configurations-table-heading">
        <h5>{GatewayListStrings.GatewayList}</h5>
      </div>
      <div style={style}>
        <>
          <span style={{ margin: '10px 17px', display: 'flex', justifyContent: 'flex-end' }}>
            {
              operationPermissions.permissions.can_add_tiq_assets
                ? <span id="top-right-btn-span">
                    <Button
                      sx={{ margin: 0, padding: '6px 20px' }}
                      onClick={() => {
                        navigate(`/create-gateway/${parentAssetId}`, { state: { parentSiteId } })
                      }}
                      width="fit-content"
                      dataTestId="create-gateway-btn"
                    >
                      {GatewayListStrings.AddNewGateway}
                    </Button>
                  </span>
                : <></>
            }
          </span>
          <EnhancedTable
            parentSiteId={parentSiteId}
            assetTypeId={assetTypeId}
            searchVal={''}
            parentAssetId={parentAssetId}
            page={page}
            rowsPerPage={rowsPerPage}
            sort={sort}
            setPage={setPage}
            setSort={setSort}
            performAction={() => {
              props.fetchGatewayList(
                parentSiteId,
                assetTypeId,
                page,
                rowsPerPage,
                '',
                sort.order,
                parentAssetId,
                sort.orderBy
              )
            }}
          />
        </>
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchGatewayList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void
  getOperationPermissions: (sub: string) => void
  fetchAssetInfo: (parentAssetId: string) => void
  downloadImage: (assetId: string, assetTypeId: string, locale: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchGatewayList: (
    parentSiteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) =>
    dispatch(
      fetchGatewayList(
        parentSiteId,
        assetTypeId,
        PageNumber,
        PageItems,
        searchKey,
        SortParams,
        parentAssetId,
        sortField
      )
    ),
  downloadImage: (assetId: string, assetTypeId: string, locale: string) => dispatch(downloadImage(assetId, assetTypeId, locale)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchAssetInfo: (parentAssetId: string) => dispatch(fetchAssetInfo(parentAssetId))
})

interface StateToProps {
  gatewayList: GatewayListState
  grandParentBreadcrumb: string
  downloadImageState: DownloadImageState
  assetInfo: AssetInfoState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  gatewayList: state.gatewayManagementGatewayList,
  assetInfo: state.gatewayManagementAssetInfo,
  grandParentBreadcrumb: state.grandParentBreadcrumb,
  downloadImageState: state.downloadImageState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(GatewayList)
